import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetContactInfos } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import ContactInfoHttpService from '../../../services/http/masterData/contactInfoHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import ContactInfoForm from './form/ContactInfoForm';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { setContactInfoMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import ContactInfoModelHelper from '../../../@types/model/masterData/contactInfo/contactInfoModelHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import UserHelper from '../../../services/helper/userHelper';
import { IContactInfo } from '../../../@types/model/masterData/contactInfo/contactInfo';
import { IContactInfoFormValues } from '../../../@types/model/masterData/contactInfo/contactInfoFormValues';

const ContactInfoScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const contactInfos = useAppSelector(x => x.masterData.contactInfos);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedContactInfo, setSelectedContactInfo] = useState<IContactInfo | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingContactInfo, setDeletingContactInfo] = useState<IContactInfo | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await ContactInfoHttpService.getContactInfoData();
                dispatch(dataSetContactInfos(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading contact info.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IContactInfoFormValues) => {
        setIsLoading(true);

        try {
            const upsert = ContactInfoModelHelper.createUpsert(values);
            const res = await ContactInfoHttpService.addOrUpdateContactInfo(upsert);
            const updatedList = upsertArrayElement(contactInfos, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetContactInfos(updatedList));
            await setContactInfoMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedContactInfo ? 'Contact info updated successfully.' : 'Contact info added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating contact info data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeContactInfo = async () => {
        if (!deletingContactInfo) return;
        const updatedContactInfo = { ...deletingContactInfo, isActive: false };
        setIsLoading(true);
        try {
            const res = await ContactInfoHttpService.deleteContactInfo(updatedContactInfo.id);
            const updatedList = upsertArrayElement(contactInfos, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetContactInfos(updatedList));
            await setContactInfoMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Contact info deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting contact info.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedContactInfo(null);
    };

    const edit = (selectedRow : IContactInfo) => {
        setSelectedContactInfo(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IContactInfo) : void => {
        setDeletingContactInfo(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingContactInfo(null);
    };

    const deleteButtonTooltip = (row : IContactInfo) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Contact Information', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return ContactInfoModelHelper.createFormValues(selectedContactInfo);
        }
    }, [selectedContactInfo, isDialogOpen]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IContactInfo>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Contact Information', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Contact Information', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IContactInfo) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        enablePagination
                        fitWidthToPage
                        columns={[
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Email', field: 'email', enableFiltering: true, enableSorting: true },
                            { title: 'Phone', field: 'telNumber', enableFiltering: true, enableSorting: true },
                            { title: 'Fax', field: 'faxNumber', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={contactInfos}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedContactInfo ? 'Edit Contact Information' : 'Add Contact Information'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <ContactInfoForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Contact Information'}
                open={!!deletingContactInfo}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeContactInfo}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default ContactInfoScreen;
